import { createSlice, nanoid, createAsyncThunk } from "@reduxjs/toolkit";
import uuid from "react-uuid";
import { getCurrentDate } from "../../utility/currentDate";
import dayjs from 'dayjs';
import { GetProforma, GetNextSequenceValue, GetProformaBasedOnProforma, GetOrganization, ValidateNIP, GetCalculatedProformaSummary } from '../../engine/baseApiClient';

const DefaultSeriesPrefix = 'F';

function setupNewInvoiceData() {
    var now = dayjs();    
    return {
        validation: {
            series: true,
            seriesPrefix: true,
            number: true,
            dateOfIssueUtc: true,
            dateOfSaleUtc: true,
            seller: {
                name: undefined,
                nip: {
                    nip: undefined
                },
                address: {
                    country: true,
                    city: undefined,
                    street: undefined,
                    houseNumber: undefined,
                    zipCode: undefined
                }
            },
            buyer: {
                name: undefined,
                isCompany: undefined,
                nip: {
                    nip: undefined
                },
                address: {
                    country: true,
                    city: undefined,
                    street: undefined,
                    houseNumber: undefined,
                    zipCode: undefined
                }
            },
            placeOfIssue: undefined,
            items: []
        },
        data: {
            id: nanoid(),
            guid: uuid(),
            series: DefaultSeriesPrefix + '/' + now.format('YYYY') + '/PROFORMA',
            seriesPrefix: DefaultSeriesPrefix,
            numberPromise: 0,
            number: 0,
            dateOfIssueUtc: now,
            dateOfSaleUtc: now,
            seller: {
                name: '',
                nip: {
                    nip: ''
                },
                address: {
                    country: 'PL',
                    city: '',
                    street: '',
                    houseNumber: '',
                    apartmentNumber: '',
                    zipCode: ''
                },
                email: '',
                phone: '',
                cellPhone: ''
            },

            buyer: {
                userId: undefined,
                name: '',
                isCompany: true,
                nip: {
                    nip: ''
                },
                address: {
                    country: 'PL',
                    city: '',
                    street: '',
                    houseNumber: '',
                    apartmentNumber: '',
                    zipCode: ''
                },
                email: '',
                phone: '',
                cellPhone: ''
            },
            currency: {
                code: 'PLN',
                exchangeRate: 1,
                exchangeRateDataUts: getCurrentDate()
            },
            payment: {
                code: 'transfer',
                deadline: 7
            },
            paymentBankAccount: undefined,
            payments: [],
            items: [],
            discounts: [],
            placeOfIssue: '',
            amountToPay: 0,
            amountPaid: 0,
            comments: '',
            bdo: '',
            signatureOfTheIssuer: '',
            signatureOfTheReceiver: '',
            settings: {
                sendEmail: false,
                emailToSend: '',
                isPeriodicInvoice: false,
                issuingPeriod: 'month',
                enabledAutomaticDocumentSend: false,
                automaticSendToEmails: [],
            }
        },
        summary: {
            vat: []
        }
    }
}

const initialState = 
    {   ...setupNewInvoiceData(), 
        isLoading: true 
    };

const toDataGridRow = (data) => {
    return { ...data, id: nanoid() };
};

const toItemDataGridRow = (data) => {
    return {
        grossValue: data.amountGross,
        id: nanoid(),
        name: data.name,
        productId: data.productId,
        pkwiu: data.pkWiU,
        netValue: data.amountNet,
        quantity: data.quantity,
        unit: data.unit,
        unitNetPrice: data.unitNetPrice,
        vat: {
            name: data.vat.name,
            value: data.vat.value
        }
    };
};

const validateNIP = async (nip, countryCode) => {
    if (nip.length >= 10) {
        const res = await ValidateNIP(nip, countryCode);
        return res.result;
    }
    else { 
        return false; 
    }
}

const setSellerValidationObjectValuesTo = (state, newValue) => {
    state.validation.seller.name = newValue;
    state.validation.seller.nip.nip = newValue;
    state.validation.seller.address.country = newValue;
    state.validation.seller.address.city = newValue;
    state.validation.seller.address.street = newValue;
    state.validation.seller.address.houseNumber = newValue;
    state.validation.seller.address.zipCode = newValue;
};

const setBuyerValidationObjectValuesTo = (state, newValue) => {
    state.validation.buyer.isCompany = newValue;
    state.validation.buyer.name = newValue;
    state.validation.buyer.nip.nip = newValue;
    state.validation.buyer.address.country = newValue;
    state.validation.buyer.address.city = newValue;
    state.validation.buyer.address.street = newValue;
    state.validation.buyer.address.houseNumber = newValue;
    state.validation.buyer.address.zipCode = newValue;
};

const setInvoiceValidationValuesTo = (state, newValue) => {
    state.validation.series = newValue;
    state.validation.number = newValue;
    state.validation.dateOfIssueUtc = newValue;
    state.validation.dateOfSaleUtc = newValue;
    setSellerValidationObjectValuesTo(state, newValue);
    setBuyerValidationObjectValuesTo(state, newValue);
    state.validation.placeOfIssue = newValue;
}

function setupInvoiceData(state, data, guid) {
    state.data.id = nanoid();
    state.data.guid = guid;
    state.data.series = data.series;
    if(data.series === undefined) {
        state.data.seriesPrefix = DefaultSeriesPrefix;
    } else {
        state.data.seriesPrefix = data.series[0];
    }

    state.data.number = data.number;
    if(data.numberPromise === undefined || data.numberPromise === '') {
        state.data.numberPromise = data.number;
    } else {
        state.data.numberPromise = data.numberPromise;
    }

    state.data.dateOfIssueUtc = dayjs(data.dateOfIssueUtc);
    state.data.dateOfSaleUtc = dayjs(data.dateOfSaleUtc);
    state.data.seller = data.seller;
    state.data.buyer = data.buyer;
    state.data.currency = {
        code: data.currency.code,
        exchangeRate: data.currency.exchangeRate,
        exchangeRateDataUts: dayjs(data.currency.exchangeRateDataUts)
    };

    state.data.payment = {
        code: data.payment.code,
        deadline: data.payment.deadline,
    };

    state.data.paymentBankAccount = data.paymentBankAccount;
    state.data.items = data.items.map((item) => toItemDataGridRow(item));
    state.data.discounts = data.discounts;
    state.data.placeOfIssue = data.placeOfIssue;
    state.data.amountToPay = data.amountToPay;
    state.data.amountPaid = data.amountPaid;
    state.data.comments = data.comments;
    state.data.bdo = data.bdo;
    if (data.settings != null) {
        state.data.settings = data.settings;
    }

    state.data.signatureOfTheIssuer = data.signatureOfTheIssuer;
    state.data.signatureOfTheReceiver = data.signatureOfTheReceiver;
}

export const doGetProformaAsync = createAsyncThunk(
    "proformaEditor/doGetProformaAsync",
    async (id) => {
        const response = await GetProforma(id);
        return response;
    }
);

export const doGetProformaSummaryAsync = createAsyncThunk(
    "proformaEditor/doGetProformaSummaryAsync",
    async (payload) => {
        return await GetCalculatedProformaSummary(payload);
    }
);

export const doGetProformaBasedOnProformaAsync = createAsyncThunk(
    "proformaEditor/doGetProformaBasedOnProformaAsync",
    async (payload) => {
        const response = await GetProformaBasedOnProforma(payload.id, payload.invoiceDateUtc, payload.timezone);
        return response;
    }
);

export const doGetNextSequenceValueAsync = createAsyncThunk(
    "proformaEditor/doGetNextSequenceValueAsync",
    async (sequence) => {
        const response = await GetNextSequenceValue(sequence);
        return response;
    }
);

export const doSetupNewProformaAsync = createAsyncThunk(
    "proformaEditor/doSetupNewProformaAsync",
    async () => {
        const organization = await GetOrganization();
        const data = setupNewInvoiceData().data;
        const response = await GetNextSequenceValue(data.series);
        data.number = 0;
        data.numberPromise = response.value;
        if (organization.organization !== undefined) {            
            data.seller = organization.organization.company;
            data.seller.readFromStorage = true;
            data.placeOfIssue = data.seller.address.city;

            if(organization.organization.automaticSendingOfDocuments !== null) {
                data.settings.enabledAutomaticDocumentSend = organization.organization.automaticSendingOfDocuments.enabled;
                data.settings.automaticSendToEmails = organization.organization.automaticSendingOfDocuments.emails.map(entry => entry.email);
            }
        }
        return data;
    }
);

export const proformaEditorSlice = createSlice({
    name: 'proformaEditor',
    initialState,
    reducers: {
        setValidationObjectValuesTo(state, action) {
            const newValue = action.payload.value;
            setInvoiceValidationValuesTo(state, newValue);
        },
        setInvoiceValidation(state, action) {
            state.validation = action.payload;
        },
        setItemFieldValidation(state, action) {
            let item = state.validation.items.find((item) => item.id === action.payload.id);

            if(item === undefined) {
                item = { id: action.payload.id };
                state.validation.items.push(item);
            }
            
            item[action.payload.field] = action.payload.valid;
        },
        setItemValidation(state, action) {
            let item = state.validation.items.find((item) => item.id === action.payload.id);

            if(item === undefined) {
                item = { ...action.payload };
                state.validation.items.push(item);
            } else {           
                item = action.payload;
            }
        },
        deleteItemValidation(state, action) {
            state.validation.items = state.validation.items.filter((item) => item.id !== action.payload);
        },
        updateInvoice: (state, action) => {
            const newValue = action.payload.value;

            switch (action.payload.type) {
                case 'series': { state.data.series = newValue; return; }
                case 'seriesPrefix': { state.data.seriesPrefix = newValue; return; }
                case 'number': {
                    state.data.number = newValue;
                    state.data.numberPromise = newValue;
                    return;
                }
                case 'dateOfIssueUtc': {
                    state.data.dateOfIssueUtc = newValue;
                    state.validation.dateOfIssueUtc = (newValue !== "");
                    return;
                }
                case 'dateOfSaleUtc': {
                    state.data.dateOfSaleUtc = newValue;
                    state.validation.dateOfSaleUtc = (newValue !== "");
                    return;
                }
                case 'currency.code': { state.data.currency.code = newValue; return; }

                case 'items': { 
                    state.data.items = newValue; 
                    return; 
                }
                case 'paymentBankAccount': { state.data.paymentBankAccount = newValue; return; }
                case 'payments': { state.data.payments = newValue; return; }
                case 'payment.code': { state.data.payment.code = newValue; return; }
                case 'payment.deadline': { state.data.payment.deadline = newValue; return; }
                case 'placeOfIssue': {
                    state.data.placeOfIssue = newValue;
                    state.validation.placeOfIssue = (newValue !== "");
                    return;
                }
                case 'amountToPay': { state.data.amountToPay = newValue; return; }
                case 'amountPaid': { state.data.amountPaid = newValue; return; }
                case 'comments': { state.data.comments = newValue; return; }
                case 'bdo': { state.data.bdo = newValue; return; }
                case 'signatureOfTheIssuer': { state.data.signatureOfTheIssuer = newValue; return; }
                case 'signatureOfTheReceiver': { state.data.signatureOfTheReceiver = newValue; return; }
                case 'settings.emailToSend': { state.data.settings.emailToSend = newValue; return; }
                case 'settings.sendEmail': { state.data.settings.sendEmail = newValue; return; }
                case 'settings.isPeriodicInvoice': { state.data.settings.isPeriodicInvoice = newValue; return; }
                case 'settings.issuingPeriod': { state.data.settings.issuingPeriod = newValue; return; }
            }
        },
        updateSeller: (state, action) => {
            state.data.seller = action.payload;
            setSellerValidationObjectValuesTo(state, true);
            state.validation.placeOfIssue = true;
        },
        updateBuyer: (state, action) => {
            state.data.buyer = action.payload.data;
            if(action.payload.buyerId !== null) {
                state.data.buyer.userId = action.payload.buyerId;
            }
            setBuyerValidationObjectValuesTo(state, true);
        },
        updateBuyerUserId: (state, action) => {
            state.data.buyer.userId = action.payload;
        },
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(doGetProformaAsync.fulfilled, (state, action) => {
                setupInvoiceData(state, action.payload.invoice, action.payload.id);
                setInvoiceValidationValuesTo(state, true);
                state.isLoading = false;
            })
            .addCase(doGetNextSequenceValueAsync.fulfilled, (state, action) => {
                state.data.numberPromise = action.payload.value;
                state.data.number = 0;
            })
            .addCase(doSetupNewProformaAsync.fulfilled, (state, action) => {
                setupInvoiceData(state, action.payload, uuid());
                state.validation = setupNewInvoiceData().validation;

                if (action.payload.seller.readFromStorage != undefined) {
                    setSellerValidationObjectValuesTo(state, action.payload.seller?.nip?.nip === '' ? undefined : true);
                    state.validation.placeOfIssue = true;
                }

                state.data.number = 0;
                state.isLoading = false;
            })
            .addCase(doGetProformaBasedOnProformaAsync.fulfilled, (state, action) => {
                setupInvoiceData(state, action.payload, uuid());
                setInvoiceValidationValuesTo(state, true);
                state.data.number = 0;
                state.isLoading = false;
            })
            .addCase(doGetProformaSummaryAsync.fulfilled, (state, action) => {
                state.summary = action.payload;
            })
    }
})

export const selectInvoice = (state) => state.proformaEditor.data;
export const selectInvoiceValidation = (state) => state.proformaEditor.validation;
export const selectPromise = (state) => state.proformaEditor.numberPromise;
export const selectPayments = (state) => state.proformaEditor.data.payments;
export const selectSummary = (state) => state.proformaEditor.summary;
export const selectIsLoading = (state) => state.proformaEditor.isLoading;

export const { storeInvoice, 
    getInvoiceData, 
    updateInvoice, 
    updateSeller, 
    updateBuyer, 
    updateBuyerUserId, 
    setValidationObjectValuesTo, 
    setInvoiceValidation,
    setItemFieldValidation,
    setItemValidation,
    setIsLoading,
    deleteItemValidation,
} = proformaEditorSlice.actions;
export default proformaEditorSlice.reducer;